// Rattrapage central des requêtes refusées faute de session valide.
//
// POURQUOI CE MODULE EXISTE
// -------------------------
// `supabase-js` ne s'arrête pas quand la session est absente, périmée, ou que le
// rafraîchissement a échoué : `_getAccessToken()` **retombe sur la clé anon**
// (`?? this.supabaseKey`). La requête part quand même, en rôle `anon`, la RLS la
// refuse, et PostgREST répond 401. Côté écran, il ne se passe RIEN : pas de
// message, pas de reconnexion, juste des données qui n'arrivent jamais.
//
// Mesuré en production le 5 août 2026 à 00:04:52 : 22 réponses 401 en 6 secondes
// sur un seul iPhone (app native), toutes issues du même montage d'écran
// calendrier (5 endpoints). La propriétaire du salon a vu un écran vide et a
// conclu que ses rendez-vous avaient disparu.
//
// Le même mécanisme est déjà documenté, pour les RPC, dans
// `src/lib/auth/requireSession.ts` — mais ce garde-fou n'est branché que sur
// deux appels. Ici on couvre le chemin REST, globalement, par le cache React
// Query (voir `src/lib/queryClient.ts`).
//
// CE QUE CE MODULE NE FAIT PAS
// ----------------------------
// Il ne déconnecte personne. Une déconnexion automatique sur 401 est précisément
// ce qui produit les boucles de reconnexion déjà vécues sur ce dépôt
// (`docs/features/auth-inscription/2026-06-08-deconnexion-complete-signout-global.md`).
// On tente un rafraîchissement, et si ça ne prend pas on le DIT à l'utilisateur.

import { supabase } from '@/integrations/supabase/client';
import { sessionExpiredToast } from './toast';

export type AuthRecoveryOutcome = 'ignored' | 'recovered' | 'signed-out';

/**
 * Levée par un chargeur de données qui constate, AVANT de partir sur le réseau,
 * qu'il n'y a pas de session vivante. Mieux vaut ça qu'une requête qui part en
 * rôle `anon` et revient en 401 : le résultat est le même pour l'utilisateur,
 * mais ici on ne consomme pas d'aller-retour et le journal est explicite.
 */
export class SessionMissingError extends Error {
  readonly code = 'SESSION_MISSING';

  constructor(context: string) {
    super(`Aucune session vivante au moment de charger "${context}".`);
    this.name = 'SessionMissingError';
  }
}

/**
 * L'erreur traduit-elle une session morte, et non un refus de droits ?
 *
 * On reste volontairement étroit. Un `42501` / 403 signifie que l'utilisateur
 * EST identifié mais n'a pas la permission : rafraîchir sa session n'y change
 * rien, et lui dire « session expirée » serait faux. Un 406 (`PGRST116`) est un
 * `.single()` sans ligne, pas un problème d'identité.
 */
export function isAuthSessionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { status?: number; code?: string; message?: string };

  if (err.code === 'PGRST301') return true;
  if (err.code === 'SESSION_MISSING') return true;
  if (err.status === 401) return true;

  return false;
}

// Un seul rafraîchissement à la fois : une rafale de 22 requêtes refusées ne
// doit pas produire 22 rafraîchissements concurrents.
let inFlight: Promise<AuthRecoveryOutcome> | null = null;

// Une fois la session déclarée morte, on cesse d'essayer. Sans ce verrou, chaque
// refetch relancerait un rafraîchissement voué à échouer, en boucle.
let sessionDeclaredDead = false;

/**
 * À appeler quand une vraie session est réinstallée (connexion, ou
 * `TOKEN_REFRESHED`) pour réarmer le rattrapage.
 */
export function resetAuthRecovery(): void {
  inFlight = null;
  sessionDeclaredDead = false;
}

/** Alias explicite pour les tests. */
export const __resetAuthRecoveryForTests = resetAuthRecovery;

/**
 * Tente de sauver une requête refusée pour cause de session. Ne lève jamais :
 * appelé depuis un gestionnaire d'erreur, il ne doit pas en produire une autre.
 */
export async function recoverFromAuthError(error: unknown): Promise<AuthRecoveryOutcome> {
  if (!isAuthSessionError(error)) return 'ignored';
  if (sessionDeclaredDead) return 'signed-out';
  if (inFlight) return inFlight;

  inFlight = (async (): Promise<AuthRecoveryOutcome> => {
    try {
      const { data, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError || !data?.session) {
        sessionDeclaredDead = true;
        sessionExpiredToast();
        return 'signed-out';
      }

      return 'recovered';
    } catch {
      sessionDeclaredDead = true;
      sessionExpiredToast();
      return 'signed-out';
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
